import { useEffect, useRef, useState } from 'react';
import {
  tryMergedResumeQuickPoll,
} from '@zr/utilities/api/mergedResume';

/**
 * Custom hook that calls mergedResume.js api requests (/api/profile/merged-resume) when initialized
 * It invokes the appropriate callback depending on the result of those api requests
 * This hook also handles an isWaiting react state, that's true when this hook is busy with api requests
 *
 * @param {function} options.onError called when an unexpected error happens, like a network error
 * @param {function} options.onMergeFailure called when merge status is 'failure'
 * @param {function} options.onMergeNeedsFix called when merge status is 'needs_fix'
 * @param {function} options.onMergeSuccess called when merge status is 'success'
 * @param {function} options.onPollLimitReached called when polling reached the max retries
 * @param {function} options.profileMethod profile_method string for analytics
 * @returns {string} output.isWaiting react state that's true when this hook is busy with api requests
 */
export function useMergeParsedResume({
  onError,
  onMergeFailure,
  onMergeNeedsFix,
  onMergeSuccess,
  onPollLimitReached,
  profileMethod,
}) {
  const [isWaiting, setIsWaiting] = useState(true);
  // ensure we do not run initialization api requests a 2nd time
  // this might happen if react re-renders or the developer is bad (me)
  const initializedRef = useRef(false);

  // if we want to do something specifically if we are waiting a long time, then use this
  // const [isPolling, setIsPolling] = useState(false);
  // const handleMergeStartPoll = () => {
  //   setIsWaiting(true);
  // };

  useEffect(() => {
    const handleMergeFailure = (response) => {
      onMergeFailure(response);
    };

    const handleMergeSuccess = (response) => {
      onMergeSuccess(response);
    };

    const handleMergeNeedsFix = (response) => {
      // if 'needs_fix' happens, then we're showing profile entry correction wizard user interface
      // we're done 'waiting'
      setIsWaiting(false);
      onMergeNeedsFix(response);
    };

    const handlePollLimitReached = (response) => {
      onPollLimitReached(response);
    };

    const runParsedResume = async () => {
      if (initializedRef.current) {
        return;
      }
      initializedRef.current = true;
      try {
        await tryMergedResumeQuickPoll({
          onMergeFailure: handleMergeFailure,
          // onMergeStartPoll: handleMergeStartPoll,
          onPollLimitReached: handlePollLimitReached,
          onMergeSuccess: handleMergeSuccess,
          onMergeNeedsFix: handleMergeNeedsFix,
          profileMethod,
        });
      } catch (e) {
        onError(e);
      }
    };

    runParsedResume();
  }, [onError, onMergeFailure, onMergeNeedsFix, onMergeSuccess, onPollLimitReached, profileMethod]);

  return {
    isWaiting,
  };
}

export default useMergeParsedResume;
